// React Core

import dayjs from 'dayjs';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

// Assets
import closeIcon from '@/assets/icon/close-icon.svg';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/Button';
// Components
import DetailModal from '@/shared/components/ui/Modal/DetailModal/DetailModal';

// Libraries & Styles
import 'air-datepicker/air-datepicker.css';
import AirDatepicker, { type AirDatepickerPosition } from 'air-datepicker';

// @ts-expect-error - AirDatepicker locale import issue
import localeKo from 'air-datepicker/locale/ko.js';

// Utilities
import Common from '@/shared/utils/standard';
// import { formatDateToYYYYMMDD } from "@/utils/date";

// Hooks
import useAuth from '@/domains/auth/hooks/useAuth';

// Plugins
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

// Types
interface FormData {
  user_id: string;
  name: string;
  birth: string;
  org: string;
  email: string;
  phone: string;
  new_pass: string;
  co_pass: string;
}

interface OptionSelect {
  value: string;
  label: string;
}

interface ModalForgotPasswordProps {
  isOpen: boolean;
  onClose: () => void;
  optionsSelect: OptionSelect[];
}

interface DataToSend extends FormData {
  epassword: string;
  eco_password: string;
}

// Extending HTMLElement to include AirDatepicker property
declare global {
  interface HTMLElement {
    _airDatepicker?: any;
  }
}

const ModalForgotPassword: React.FC<ModalForgotPasswordProps> = ({
  isOpen,
  onClose,
  optionsSelect,
}) => {
  const [formData, setFormData] = useState<FormData>({
    user_id: '',
    name: '',
    birth: '',
    org: '',
    email: '',
    phone: '',
    new_pass: '',
    co_pass: '',
  });
  useEffect(() => {
    if (isOpen) {
      // TODO: Replace direct DOM access with React ref or state
      const inputElement = document.querySelector('[name="birth"]') as HTMLInputElement;
      if (inputElement && !inputElement._airDatepicker) {
        const optionsDate = {
          autoClose: true,
          locale: localeKo,
          position: 'top center' as AirDatepickerPosition,
          onSelect: (date: { formattedDate: string | string[] }) => {
            const formatted = Array.isArray(date.formattedDate)
              ? date.formattedDate[0]
              : date.formattedDate;
            setFormData((prevValues) => ({
              ...prevValues,
              birth: dayjs(formatted).format('YYYY-MM-DD'),
            }));
            console.log('Selected Date:', dayjs(formatted).format('YYYY-MM-DD'));
          },
        };
        new AirDatepicker(inputElement, optionsDate);
      }
    }
  }, [isOpen]);

  const { handleForgotPassword } = useAuth({
    onResetFail: (err?: any) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(err?.message);
    },
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (): Promise<void> => {
    const dataToSend: DataToSend = {
      user_id: formData.user_id,
      name: formData.name,
      birth: formData.birth,
      org: formData.org,
      email: formData.email,
      phone: formData.phone,
      new_pass: formData.new_pass,
      co_pass: formData.co_pass,
      epassword: Common.SHA256(formData.new_pass.trim()), // Hash
      eco_password: Common.SHA256(formData.co_pass.trim()), // Hash
    };

    console.log('Form Data to Send:', dataToSend);
    await handleForgotPassword(dataToSend);
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center transition-all duration-300',
        isOpen ? 'visible opacity-100' : 'invisible opacity-0'
      )}
    >
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}></div>
      <div
        className="bg-white dark:bg-db-wrap rounded-xl w-[48%] shadow-lg relative z-10 transition-transform duration-300 transform scale-95"
        style={{ padding: '48px' }}
      >
        {/* TODO: i18n - hardcoded Korean string */}
        <div className="flex items-center justify-between w-full">
          <span className="font-semibold text-[29px]">비밀번호를 잊으셨나요?</span>
          <Button
            variant="ghost"
            type="button"
            className="modal-close p-0 h-auto hover:bg-transparent cursor-pointer"
            onClick={onClose}
          >
            <img src={closeIcon} alt="Close" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="flex flex-col gap-4">
            <DetailModal
              label="아이디"
              inputType="text"
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              modalType="forgot"
            />
            <DetailModal
              label="이름"
              inputType="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              modalType="forgot"
            />
            <DetailModal
              label="생일"
              inputType="text"
              name="birth"
              value={formData.birth}
              isDob={true}
              onChange={handleChange}
              modalType="forgot"
            />
            <DetailModal
              label="전화번호"
              inputType="text"
              pattern={/^[0-9]*$/}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              modalType="forgot"
            />
          </div>

          <div className="flex flex-col gap-4">
            <DetailModal
              label="조직"
              inputType="select"
              optionSelect={optionsSelect}
              name="org"
              value={formData.org}
              onChange={handleChange}
              modalType="forgot"
            />

            <DetailModal
              label="이메일"
              inputType="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              modalType="forgot"
            />

            <DetailModal
              label="새 비밀번호"
              inputType="password"
              name="new_pass"
              value={formData.new_pass}
              onChange={handleChange}
              maxLength={20}
              modalType="forgot"
            />

            <DetailModal
              label="비밀번호 확인"
              inputType="password"
              name="co_pass"
              value={formData.co_pass}
              onChange={handleChange}
              maxLength={20}
              modalType="forgot"
            />
          </div>
        </div>

        <div className="_boxBtnForgot">
          <Button
            label="새로운 비밀번호 생성"
            customButton="button-forgot dark:text-white!"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalForgotPassword;
